import React, {
  useState,
  useMemo,
  memo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useQuery } from "react-query";
import axios from "../../config/axiosInstance";
import { USERS, TAGS, PAYMENTS } from "../../config/apiConfig";
import DrawerLoading from "../DrawerLoading";
import ForbiddenQueriesPermission from "../common/ForbiddenQueriesPermission";
import CustomCreditQuery from "../queries/CustomCreditQuery";
import {
  FaSearch,
  FaTools,
  FaFilter,
  FaTimes,
  FaBalanceScale,
} from "react-icons/fa";
import { HiUser, HiOfficeBuilding } from "react-icons/hi";
import { motion, useReducedMotion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { formatToReal } from "../../utils/utils";
import { useApplication } from "../../contexts/ApplicationContext";

/* ===================== helpers ===================== */
const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const getQueryTagIds = (q) => {
  const fromObjects = (arr, key = "id") =>
    (arr || []).map((o) => toNum(o?.[key])).filter((n) => n !== null);

  if (Array.isArray(q?.Tags)) return fromObjects(q.Tags, "id");
  if (Array.isArray(q?.tags)) return fromObjects(q.tags, "id");
  if (Array.isArray(q?.QueryTags)) return fromObjects(q.QueryTags, "TagId");
  if (Array.isArray(q?.tagIds))
    return (q.tagIds || []).map(toNum).filter((n) => n !== null);
  if (Array.isArray(q?.tagsIds))
    return (q.tagsIds || []).map(toNum).filter((n) => n !== null);
  return [];
};

const normalize = (s) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const matchesSearch = (query, term) => {
  if (!term) return true;
  const t = normalize(term);
  return normalize(query?.name).includes(t);
};

// OR: contém ao menos uma tag selecionada
const matchesTagFilterOR = (query, selectedTagIds) => {
  if (!selectedTagIds.length) return true;
  const qTags = getQueryTagIds(query);
  return selectedTagIds.some((id) => qTags.includes(id));
};

// detecta tipo (CPF/CNPJ)
const detectType = (q) => {
  const t = q?.QueryCustomized?.parameterType || q?.parameterType;
  if (t === "CPF" || t === "CNPJ" || t === "CPF/CNPJ") return t;
  const name = (q?.name || "").toLowerCase();
  if (name.includes("cpf")) return "CPF";
  if (name.includes("cnpj") || name.includes("empresa")) return "CNPJ";
  return "";
};

// animação painel de detalhes
const queryPanelVariants = {
  hidden: { opacity: 0, x: 24 },
  show: {
    opacity: 1,
    x: 0,
    transition: { type: "spring", stiffness: 220, damping: 22 },
  },
  exit: { opacity: 0, x: 16, transition: { duration: 0.16 } },
};

// animação dos cards (em viewport)
const cardVariants = {
  hidden: { opacity: 0, y: 4 },
  show: { opacity: 1, y: 0 },
};

/* ===================== Modal Genérico ===================== */
const Modal = ({ open, onClose, title, children }) => {
  const dialogRef = useRef(null);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") onClose?.();
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", handleKeyDown);
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = overflow;
    };
  }, [open, handleKeyDown]);

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-center justify-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/40"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          {/* Conteúdo */}
          <motion.div
            ref={dialogRef}
            className="relative mx-3 w-full max-w-2xl rounded-2xl bg-white shadow-2xl border border-gray-200 overflow-hidden"
            initial={{ y: 24, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 12, opacity: 0, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 240, damping: 22 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Cabeçalho */}
            <div className="bg-gradient-to-r from-blue-600 to-green-500 px-5 py-4 text-white">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/15">
                    <svg
                      viewBox="0 0 24 24"
                      className="w-4.5 h-4.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M20.59 13.41L11 3.83A2 2 0 009.59 3H4a1 1 0 00-1 1v5.59A2 2 0 003.83 11l9.58 9.59a2 2 0 002.83 0l4.35-4.35a2 2 0 000-2.83z" />
                      <circle cx="7.5" cy="7.5" r="1.5" />
                    </svg>
                  </span>
                  <div className="leading-tight">
                    <h2
                      id="modal-title"
                      className="text-sm sm:text-base font-semibold"
                    >
                      {title}
                    </h2>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="rounded-md px-3 py-1.5 text-[12px] bg-white/10 hover:bg-white/20 transition"
                  aria-label="Fechar"
                  title="Fechar"
                >
                  Fechar
                </button>
              </div>
            </div>
            {/* Corpo */}
            <div className="p-4 sm:p-5 max-h-[70vh] overflow-y-auto">
              {children}
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

/* ===================== Card da Consulta ===================== */
const truncateText = (value, limit = 80) => {
  if (!value) return "";
  return value.length > limit ? `${value.slice(0, limit - 3)}...` : value;
};

const QueryCard = memo(function QueryCard({
  query,
  index,
  allTags,
  handleQueryClick,
}) {
  const { user } = useApplication();
  const type = detectType(query);
  const TypeIcon = type === "CNPJ" ? HiOfficeBuilding : HiUser;
  const isDualType = type === "CPF/CNPJ";
  const statusActive = query?.status == 1;
  const tagNames = useMemo(() => {
    const ids = getQueryTagIds(query);
    return ids
      .map((id) => allTags.find((t) => t.id === id))
      .filter(Boolean)
      .map((t) => t.name)
      .slice(0, 3);
  }, [allTags, query]);
  const showPrice =
    user?.Company?.showPriceQuery && Number.isFinite(Number(query?.price));
  const priceLabel = showPrice
    ? `${formatToReal(query.price)}/consulta`
    : "";

  return (
    <motion.div
      key={query.id}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.15 }}
      variants={cardVariants}
      transition={{
        duration: 0.22,
        ease: "easeOut",
        delay: Math.min(index * 0.03, 0.18),
      }}
      onClick={statusActive ? () => handleQueryClick(query) : undefined}
      className={`relative group cursor-pointer rounded-2xl border bg-white p-4 shadow-sm transition-all duration-300 ${
        statusActive
          ? "border-slate-200 hover:-translate-y-1 hover:border-blue-200 hover:shadow-md"
          : "border-gray-300 opacity-70 pointer-events-none shadow-sm"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <span
            className={`rounded-full p-2 ${
              statusActive ? "bg-blue-50" : "bg-gray-100"
            }`}
          >
            {isDualType ? (
              <div className="flex items-center gap-1 text-lg">
                <HiUser
                  className={`text-base ${
                    statusActive ? "text-blue-700" : "text-gray-500"
                  }`}
                />
                <HiOfficeBuilding
                  className={`text-base ${
                    statusActive ? "text-blue-700" : "text-gray-500"
                  }`}
                />
              </div>
            ) : (
              <TypeIcon
                className={`text-xl ${
                  statusActive ? "text-blue-700" : "text-gray-500"
                }`}
              />
            )}
          </span>
          <div className="min-w-0 flex-1">
            <h3
              className="text-[13px] font-semibold text-slate-900 uppercase truncate overflow-hidden max-w-[220px]"
              title={query.name}
            >
              {query.name}
            </h3>
            <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">
              {type}
            </p>
          </div>
        </div>
      </div>

      <p className="mt-2 text-[11px] text-slate-500">
        {truncateText(
          query?.description ||
            "Consulta completa com informações oficiais e referências atualizadas.",
          80
        )}
      </p>

      <div className="mt-3 flex items-center justify-between text-[12px] text-slate-500">
        <span className="font-semibold text-slate-900">{priceLabel}</span>
        {statusActive && (
          <span className="text-[12px] font-semibold text-white rounded-full bg-gradient-to-r from-blue-600 to-blue-800 px-3 py-1 shadow-sm">
            CONSULTAR
          </span>
        )}
      </div>
    </motion.div>
  );
});

/* ===================== Principal ===================== */
const QueriesCreditTable = () => {
  const navigate = useNavigate();
  const { userPermission } = useApplication();
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [selectedPrice, setSelectedPrice] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [isListVisible, setIsListVisible] = useState(true);

  // abas: ALL | CPF | CNPJ
  const [activeTab, setActiveTab] = useState("ALL");

  // modal: guarda a query cujas tags vamos exibir
  const [modalQuery, setModalQuery] = useState(null);

  // filtro de tags
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [tagSearch, setTagSearch] = useState("");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  // queries do usuário
  const { isLoading, data: userQueryPermissions } = useQuery(
    ["userQueryPermissions"],
    async () => {
      const { data: items } = await axios.get(USERS.GET_USER_QUERY_PERMISSIONS);
      return items?.data?.filter(
        (item) =>
          item.url !== "motor-credito-pf" && item.url !== "motor-credito-pj"
      );
    }
  );

  // tags
  const { isLoading: isLoadingTags, data: tagsPayload } = useQuery(
    ["credit-tags"],
    async () => {
      const { data } = await axios.get(TAGS.GET_ALL_SIMPLE);
      return Array.isArray(data?.data) ? data.data : data;
    },
    { staleTime: 1000 * 60 * 10 }
  );

  const allTags = useMemo(() => tagsPayload || [], [tagsPayload]);
  const isAdminUser = Array.isArray(userPermission)
    ? userPermission.includes("ADMIN")
    : userPermission === "ADMIN";

  const { data: boletoStatus } = useQuery(
    ["open-boleto-alert"],
    async () => {
      const { data } = await axios.get(
        `${PAYMENTS.GET_ALL}?isPaid=false&itemsPerPage=-1`
      );
      const rows = data?.data?.data || [];
      const boletos = rows.filter((inv) => inv?.type === "BOLETO");
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const overdue = boletos.filter((inv) => {
        if (!inv?.dueDate) return false;
        const due = new Date(inv.dueDate);
        return due < today;
      }).length;
      return { total: boletos.length, overdue };
    },
    {
      enabled: isAdminUser,
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );

  const tagCounts = useMemo(() => {
    const counts = new Map();
    (userQueryPermissions || []).forEach((q) => {
      getQueryTagIds(q).forEach((id) => {
        counts.set(id, (counts.get(id) || 0) + 1);
      });
    });
    return counts;
  }, [userQueryPermissions]);

  const filteredTags = useMemo(() => {
    const s = tagSearch.trim().toLowerCase();
    if (!s) return allTags;
    return allTags.filter((t) => t.name.toLowerCase().includes(s));
  }, [allTags, tagSearch]);

  // 1) aplica busca + OR de tags
  const filteredQueriesBase = useMemo(() => {
    return (userQueryPermissions || []).filter(
      (q) =>
        matchesSearch(q, searchTerm) && matchesTagFilterOR(q, selectedTagIds)
    );
  }, [userQueryPermissions, searchTerm, selectedTagIds]);

  // 2) filtra por aba
  const filteredByTab = useMemo(() => {
    if (activeTab === "ALL") return filteredQueriesBase;
    return filteredQueriesBase.filter((q) => detectType(q) === activeTab);
  }, [filteredQueriesBase, activeTab]);

  // 3) filtra somente categoria Crédito (id=1), status válido e não adicionais
  const sortedQueries = useMemo(
    () =>
      filteredByTab
        ?.filter((q) => {
          const isAdditional =
            typeof q.isAdditional === "boolean"
              ? q.isAdditional
              : Number(q.isAdditional) === 1;
          return q.categoryId === 1 && q.status != -1 && !isAdditional;
        })
        ?.sort((a, b) => a?.name?.localeCompare(b.name)),
    [filteredByTab]
  );

  // contadores por aba
  const tabCounts = useMemo(() => {
    const all = (userQueryPermissions || []).filter((q) => {
      const isAdditional =
        typeof q.isAdditional === "boolean"
          ? q.isAdditional
          : Number(q.isAdditional) === 1;
      return q.categoryId === 1 && q.status != -1 && !isAdditional;
    });
    const cpf = all.filter((q) => detectType(q) === "CPF");
    const cnpj = all.filter((q) => detectType(q) === "CNPJ");
    return { ALL: all.length, CPF: cpf.length, CNPJ: cnpj.length };
  }, [userQueryPermissions]);

  const toggleTag = (id) => {
    const nid = Number(id);
    setSelectedTagIds((prev) =>
      prev.includes(nid) ? prev.filter((x) => x !== nid) : [...prev, nid]
    );
  };

  const selectAllFiltered = () => {
    const onlyIds = (filteredTags || []).map((t) => Number(t.id));
    setSelectedTagIds((prev) => Array.from(new Set([...prev, ...onlyIds])));
  };
  const clearAllTags = () => setSelectedTagIds([]);

  const handleQueryClick = (query) => {
    setSelectedQuery(query);
    setSelectedPrice(query.price || 0);
    setIsListVisible(false);
  };

  const handleBackClick = () => {
    setIsListVisible(true);
    setSelectedQuery(null);
  };

  const prefersReduced = useReducedMotion();

  // dados da modal: tags da query selecionada
  const modalTagObjects = useMemo(() => {
    if (!modalQuery) return [];
    const ids = getQueryTagIds(modalQuery);
    return ids.map((id) => allTags.find((t) => t.id === id)).filter(Boolean);
  }, [modalQuery, allTags]);

  const groupedModalTags = useMemo(() => {
    const groups = modalTagObjects.reduce((acc, t) => {
      const letter = (t.name?.[0] || "#").toUpperCase();
      (acc[letter] ||= []).push(t);
      return acc;
    }, {});
    const letters = Object.keys(groups).sort();
    return { groups, letters };
  }, [modalTagObjects]);

  const selectedTagsLocal = useMemo(
    () => (allTags || []).filter((t) => selectedTagIds.includes(t.id)),
    [allTags, selectedTagIds]
  );

  const activeFilters = selectedTagIds.length > 0 || !!searchTerm;

  if (!userQueryPermissions?.length && !isLoading)
    return <ForbiddenQueriesPermission />;

  const renderTagFilterContent = () => (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="relative w-full sm:max-w-xs">
          <FaSearch
            className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"
            size={12}
          />
          <input
            type="text"
            placeholder="Buscar campo..."
            value={tagSearch}
            onChange={(e) => setTagSearch(e.target.value)}
            className="w-full pl-7 pr-3 py-2 rounded-lg border border-gray-300 text-xs focus:outline-none focus:ring-2 focus:ring-blue-600/30"
          />
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={selectAllFiltered}
            className="px-2 py-1 rounded-md border border-gray-300 text-xs hover:bg-gray-50 disabled:opacity-50"
            disabled={!filteredTags.length}
          >
            Selecionar filtradas
          </button>
          <button
            type="button"
            onClick={() => {
              setSelectedTagIds([]);
              setTagSearch("");
            }}
            className="px-2 py-1 rounded-md border border-gray-300 text-xs hover:bg-gray-50 disabled:opacity-50"
            disabled={!selectedTagIds.length && !tagSearch}
          >
            Limpar
          </button>
        </div>
      </div>

      {!!selectedTagsLocal.length && (
        <div className="-mx-1 overflow-x-auto">
          <div className="flex items-center gap-1.5 px-1 py-1 no-scrollbar">
            {selectedTagsLocal.map((t) => (
              <span
                key={t.id}
                className="inline-flex items-center gap-1.5 text-[11px] leading-4 px-2.5 py-1 rounded-full bg-white text-gray-700 border border-gray-300 shadow-sm hover:bg-gray-50"
                title={t.name}
              >
                <span className="truncate max-w-[42vw] sm:max-w-[220px]">
                  {t.name}
                </span>
                <button
                  type="button"
                  onClick={() => toggleTag(t.id)}
                  className="rounded-full w-4 h-4 flex items-center justify-center border border-gray-300 text-gray-600 hover:bg-gray-100 shrink-0"
                  aria-label={`Remover ${t.name}`}
                  title="Remover"
                >
                  <FaTimes className="scale-90" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-gray-50 p-2 max-h-60 overflow-auto">
        <div className="grid [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))] gap-2">
          {(filteredTags || []).map((tag) => {
            const active = selectedTagIds.includes(tag.id);
            const count = tagCounts.get(tag.id) || 0;
            return (
              <button
                key={tag.id}
                type="button"
                onClick={() => toggleTag(tag.id)}
                className={`w-full rounded-lg border px-2.5 py-2 text-left text-[12px] sm:text-sm transition-all
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/30
                  ${
                    active
                      ? "bg-white text-blue-700 border-blue-600 shadow"
                      : "bg-white text-gray-800 border-gray-300 hover:bg-gray-100"
                  }`}
                title={`${tag.name} • ${count} consulta(s)`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate" title={tag.name}>
                    {tag.name}
                  </span>
                  <span
                    className={`shrink-0 inline-flex items-center justify-center h-4 min-w-[18px] px-1.5 text-[10px] rounded-full
                      ${
                        active
                          ? "bg-blue-50 text-blue-700 border border-blue-600/40"
                          : "bg-gray-50 text-gray-600 border border-gray-200"
                      }`}
                  >
                    {count}
                  </span>
                </div>
              </button>
            );
          })}
          {!filteredTags?.length && (
            <span className="col-span-full text-xs text-gray-500">
              Nenhuma tag encontrada.
            </span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full px-1 md:px-4 pt-4 min-h-screen">
      {isListVisible ? (
        <>
          {isAdminUser && boletoStatus?.total > 0 && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex md:justify-between gap-2 md:items-center">
              <div className="flex flex-col">
                <div className="font-semibold">Atenção</div>
                <div className="text-[13px]">
                  Você tem {boletoStatus.total} boleto
                  {boletoStatus.total === 1 ? "" : "s"} em aberto
                  {boletoStatus.overdue > 0
                    ? `, sendo ${boletoStatus.overdue} atrasado${
                        boletoStatus.overdue === 1 ? "" : "s"
                      }`
                    : ""}
                  .
                </div>
              </div>

              <Link
                to="/invoices"
                className="text-xs font-medium text-amber-900 underline hover:text-amber-700"
              >
                Ir para minhas faturas
              </Link>
            </div>
          )}
          {/* Filtros */}
          <div className="mb-4 space-y-3">
            {/* Busca por nome */}
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar consultas de crédito..."
                  className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 shadow-sm focus:ring-2 focus:ring-blue-600/30 focus:outline-none text-sm sm:text-base placeholder-gray-400 transition-all"
                />
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-700 pointer-events-none">
                  <FaSearch size={18} />
                </div>

                {activeFilters && (
                  <button
                    onClick={() => {
                      setSearchTerm("");
                      setSelectedTagIds([]);
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 text-sm flex items-center gap-1"
                    title="Limpar filtros"
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => navigate("/queries/compare/pick")}
                className="inline-flex items-center gap-2 px-5  md:px-3 py-3.5 rounded-xl border text-sm transition bg-blue-800 text-white border-gray-300"
                title="Ir para seleção de consultas"
              >
                <FaBalanceScale />
                <span className="hidden md:block">Comparar Consultas</span>
              </button>
              <button
                type="button"
                onClick={() => setIsFilterModalOpen(true)}
                className="inline-flex items-center gap-2 px-5  md:px-3 py-3.5 rounded-xl border text-sm transition bg-white text-gray-800 border-gray-300 "
                title="Filtrar por campos"
              >
                <FaFilter />
                <span className="hidden md:block">Filtrar por campos</span>
              </button>
            </div>
          </div>

          {/* Resultado */}
          <div className="flex items-center justify-between mb-2">
            {/* Abas (Todas | CPF | CNPJ) */}
            <div className="">
              <div className="inline-flex rounded-2xl bg-white p-1 border border-gray-200 shadow-sm">
                {[
                  {
                    key: "ALL",
                    label: "Todas",
                    icon: null,
                    count: tabCounts.ALL,
                  },
                  {
                    key: "CPF",
                    label: "CPF",
                    icon: HiUser,
                    count: tabCounts.CPF,
                  },
                  {
                    key: "CNPJ",
                    label: "CNPJ",
                    icon: HiOfficeBuilding,
                    count: tabCounts.CNPJ,
                  },
                ].map(({ key, label, icon: Icon, count }) => {
                  const active = activeTab === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setActiveTab(key)}
                      className={`relative inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-xl text-sm transition
                      ${
                        active
                          ? "bg-blue-900 text-white shadow border border-gray-200"
                          : "text-gray-600 hover:text-gray-800"
                      }
                    `}
                      aria-pressed={active}
                    >
                      {Icon ? <Icon className="text-[16px]" /> : null}
                      <span className="font-medium">{label}</span>
                      <span
                        className={`ml-1 inline-flex items-center justify-center h-5 min-w-[22px] px-1.5 rounded-full text-[11px]
                        ${
                          active
                            ? "bg-gray-100 text-gray-800"
                            : "bg-white/70 text-gray-700 border border-gray-200"
                        }
                      `}
                      >
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            <span className="text-sm text-gray-600 hidden md:block">
              {sortedQueries?.length || 0} consulta
              {sortedQueries?.length === 1 ? "" : "s"} encontrada
              {activeFilters ? " com os filtros atuais." : "."}
            </span>
          </div>

          {/* Grid de Cards */}
          <div
            style={{ minHeight: "300px" }}
            className="overflow-hidden relative items-start grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4 mb-6 bg-gray-50 rounded-md p-4 shadow-sm border border-gray-200"
          >
            {(isLoading || isLoadingTags) && <DrawerLoading />}
            {sortedQueries?.map((query, index) => (
              <QueryCard
                key={query.id}
                query={query}
                index={index}
                onOpenModal={setModalQuery}
                allTags={allTags}
                handleQueryClick={handleQueryClick}
              />
            ))}
            {!sortedQueries?.length && !isLoading && (
              <div className="col-span-full text-center text-sm text-gray-500 py-10">
                Nenhuma consulta corresponde aos filtros aplicados.
              </div>
            )}
          </div>
        </>
      ) : (
        <div>
          <button
            onClick={handleBackClick}
            className="flex items-center text-sm text-blue-700 hover:text-blue-900 font-medium mb-4 transition-all"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Voltar para a listagem
          </button>

          <motion.div
            variants={queryPanelVariants}
            initial="hidden"
            animate="show"
            exit="exit"
          >
            <CustomCreditQuery
              query={selectedQuery}
              selectedPrice={selectedPrice}
              userQueryPermissions={userQueryPermissions}
            />
          </motion.div>
        </div>
      )}

      {/* Modal de filtros */}
      <Modal
        open={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        title="Filtrar por campos"
      >
        {renderTagFilterContent()}
      </Modal>
    </div>
  );
};

export default QueriesCreditTable;
